import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { AuthLayout } from './layouts/AuthLayout';
import { AppLayout } from './layouts/AppLayout';
import { RequireAuth } from './components/RequireAuth';
import { useAuth } from './lib/use-auth';

import { Login } from './pages/Login';
import { Punch } from './pages/Punch';
import { MyDay } from './pages/MyDay';
import { Requests } from './pages/Requests';
import { Team } from './pages/Team';
import { TeamCalendar } from './pages/TeamCalendar';
import { EmployeeDay } from './pages/EmployeeDay';
import { Settings } from './pages/Settings';
import { Reports } from './pages/Reports';
import { Stats } from './pages/Stats';
import { Documents } from './pages/Documents';
import { Tickets } from './pages/Tickets';
import { TicketFolder } from './pages/TicketFolder';
import { Admin } from './pages/Admin';
import { AdminUsersList } from './pages/admin/AdminUsersList';
import { AdminUserNew } from './pages/admin/AdminUserNew';
import { AdminUserDetailPage } from './pages/admin/AdminUserDetail';
import { AdminHolidays } from './pages/admin/AdminHolidays';
import { AdminAudit } from './pages/admin/AdminAudit';
import { AdminProjects } from './pages/admin/AdminProjects';
import { AdminDocuments } from './pages/admin/AdminDocuments';
import { AdminPayrollImport } from './pages/admin/AdminPayrollImport';
import { AdminTrips } from './pages/admin/AdminTrips';
import { AdminTripDetail } from './pages/admin/AdminTripDetail';
import { AdminTicketFolder } from './pages/admin/AdminTicketFolder';
import { AdminSettings } from './pages/admin/AdminSettings';
import { AdminBackup } from './pages/admin/AdminBackup';
import { ChangePassword } from './pages/ChangePassword';
import { PasswordResetRequest } from './pages/PasswordResetRequest';
import { PasswordResetConfirm } from './pages/PasswordResetConfirm';
import { PrivacyPolicyPublic } from './pages/PrivacyPolicyPublic';

// El manual carga su markdown completo; lo separamos en un chunk perezoso para
// no penalizar el arranque de la app.
const Help = lazy(() => import('./pages/Help').then((m) => ({ default: m.Help })));

export default function App() {
  // Feature flag servido por el backend en /auth/me. Si el bloque
  // Documentos está desactivado no montamos sus rutas: el acceso directo
  // por URL cae en el catch-all `*` y redirige a "/". Ausencia de info
  // (backend antiguo) = habilitado, el comportamiento histórico.
  const { user } = useAuth();
  const documentsEnabled = user?.features?.documents ?? true;

  return (
    <Routes>
      {/* Rutas no autenticadas */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<Login />} />
        <Route path="/password/reset" element={<PasswordResetRequest />} />
        <Route path="/password/reset/confirm" element={<PasswordResetConfirm />} />
      </Route>

      {/* Política de privacidad pública — accesible sin login. */}
      <Route path="/legal/privacidad" element={<PrivacyPolicyPublic />} />

      {/* Pantalla de cambio de contraseña — autenticada pero accesible
          incluso cuando passwordChangeRequired bloquea el resto. */}
      <Route
        path="/password/change"
        element={
          <RequireAuth>
            <ChangePassword />
          </RequireAuth>
        }
      />

      {/* Rutas autenticadas */}
      <Route
        element={
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        }
      >
        <Route path="/" element={<Punch />} />
        <Route path="/jornada" element={<MyDay />} />
        <Route path="/solicitudes" element={<Requests />} />
        <Route
          path="/equipo"
          element={
            <RequireAuth roles={['MANAGER', 'HR_ADMIN', 'SUPER_ADMIN']}>
              <Team />
            </RequireAuth>
          }
        />
        <Route path="/equipo/calendario" element={<TeamCalendar />} />
        <Route
          path="/equipo/:userId"
          element={
            <RequireAuth roles={['MANAGER', 'HR_ADMIN', 'SUPER_ADMIN']}>
              <EmployeeDay />
            </RequireAuth>
          }
        />
        <Route path="/estadisticas" element={<Stats />} />
        {documentsEnabled && <Route path="/documentos" element={<Documents />} />}
        <Route path="/tickets" element={<Tickets />} />
        <Route path="/tickets/:id" element={<TicketFolder />} />
        <Route path="/ajustes" element={<Settings />} />
        <Route
          path="/ayuda"
          element={
            <Suspense
              fallback={<div className="p-8 text-sm text-muted-foreground">Cargando manual…</div>}
            >
              <Help />
            </Suspense>
          }
        />
        <Route
          path="/reportes"
          element={
            <RequireAuth roles={['MANAGER', 'HR_ADMIN', 'SUPER_ADMIN']}>
              <Reports />
            </RequireAuth>
          }
        />
        <Route
          path="/admin"
          element={
            <RequireAuth roles={['HR_ADMIN', 'SUPER_ADMIN']}>
              <Admin />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/users"
          element={
            <RequireAuth roles={['HR_ADMIN', 'SUPER_ADMIN']}>
              <AdminUsersList />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/users/new"
          element={
            <RequireAuth roles={['HR_ADMIN', 'SUPER_ADMIN']}>
              <AdminUserNew />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/users/:id"
          element={
            <RequireAuth roles={['HR_ADMIN', 'SUPER_ADMIN']}>
              <AdminUserDetailPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/festivos"
          element={
            <RequireAuth roles={['HR_ADMIN', 'SUPER_ADMIN']}>
              <AdminHolidays />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/proyectos"
          element={
            <RequireAuth roles={['HR_ADMIN', 'SUPER_ADMIN']}>
              <AdminProjects />
            </RequireAuth>
          }
        />
        {documentsEnabled && (
          <>
            <Route
              path="/admin/documentos"
              element={
                <RequireAuth roles={['MANAGER', 'HR_ADMIN', 'SUPER_ADMIN']}>
                  <AdminDocuments />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/documentos/importar"
              element={
                <RequireAuth roles={['MANAGER', 'HR_ADMIN', 'SUPER_ADMIN']}>
                  <AdminPayrollImport />
                </RequireAuth>
              }
            />
          </>
        )}
        {/* Ruta antigua: redirige a la nueva gestión de viajes. */}
        <Route path="/admin/tickets" element={<Navigate to="/admin/viajes" replace />} />
        <Route
          path="/admin/viajes"
          element={
            <RequireAuth roles={['HR_ADMIN', 'SUPER_ADMIN']}>
              <AdminTrips />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/viajes/:id"
          element={
            <RequireAuth roles={['HR_ADMIN', 'SUPER_ADMIN']}>
              <AdminTripDetail />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/tickets/:id"
          element={
            <RequireAuth roles={['HR_ADMIN', 'SUPER_ADMIN']}>
              <AdminTicketFolder />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/auditoria"
          element={
            <RequireAuth roles={['HR_ADMIN', 'SUPER_ADMIN']}>
              <AdminAudit />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/ajustes"
          element={
            <RequireAuth roles={['HR_ADMIN', 'SUPER_ADMIN']}>
              <AdminSettings />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/copia-de-seguridad"
          element={
            <RequireAuth roles={['HR_ADMIN', 'SUPER_ADMIN']}>
              <AdminBackup />
            </RequireAuth>
          }
        />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
